import { z } from 'zod';
/**
 * Mô tả ảnh do skill vision `describe_image` sinh **một lần lúc chèn** — APM-SLD-n,
 * docs/plan-slides.md §3.10. Lưu ở thuộc tính `description` của node `image`
 * trong `.chilldoc`, đi theo tài liệu; mọi lượt sinh slide / dán thông minh về
 * sau đọc nó thay vì gửi ảnh cho model.
 *
 * Bốn trường, không phải một câu văn: `summary` để model chọn ảnh có hợp slide
 * không, `kind` + `textHeavy` để quyết crop hay không (sơ đồ, biểu đồ, ảnh chữ
 * không bao giờ crop — §3.11), `focal` để crop `cover` không cắt mất chủ thể.
 */
export const IMAGE_KINDS = [
    'photo',
    'diagram',
    'chart',
    'screenshot',
    'logo',
    'text',
    'other',
];
export const ImageKind = z.enum(IMAGE_KINDS);
export const ImageFocal = z.object({
    x: z.enum(['left', 'center', 'right']),
    y: z.enum(['top', 'center', 'bottom']),
});
export const IMAGE_SUMMARY_MAX_CHARS = 200;
export const ImageDescription = z.object({
    /** Ảnh chụp gì, một câu, cùng ngôn ngữ với tài liệu (tiếng Việt có dấu). */
    summary: z.string().trim().min(1).max(IMAGE_SUMMARY_MAX_CHARS),
    kind: ImageKind,
    /** Nhiều chữ cần đọc (bảng, slide chụp màn hình, tài liệu scan) ⇒ không crop. */
    textHeavy: z.boolean(),
    /** Chủ thể chính nằm đâu — điểm neo khi phải crop. */
    focal: ImageFocal,
});
/** Đọc thuộc tính `description` của node ảnh: rác (tệp cũ, tay sửa) ⇒ `null`, không ném. */
export function imageDescriptionOf(value) {
    if (!value || typeof value !== 'object')
        return null;
    const parsed = ImageDescription.safeParse(value);
    return parsed.success ? parsed.data : null;
}
// ── Đường mô tả: bản thu nhỏ lên S3, rồi lượt AI ─────────────────────────────
/** Trần một bản thu nhỏ — web dựng ≤ 1024 px WebP, một ảnh thường dưới 200 KB. */
export const IMAGE_DESCRIBE_MAX_BYTES = 1024 * 1024;
export const IMAGE_DESCRIBE_MAX_EDGE = 1024;
export const IMAGE_DESCRIBE_MIME_TYPES = ['image/webp', 'image/png', 'image/jpeg'];
/** `POST /me/files/image-description/upload` — xin URL PUT thẳng S3 cho bản thu nhỏ (luật #3). */
export const ImageDescribeUploadInput = z.object({
    mimeType: z.enum(IMAGE_DESCRIBE_MIME_TYPES),
    sizeBytes: z.number().int().min(1).max(IMAGE_DESCRIBE_MAX_BYTES),
});
export const ImageDescribeUpload = z.object({
    scratchId: z.uuid(),
    uploadUrl: z.string(),
    expiresInSec: z.number().int(),
    requiredHeaders: z.record(z.string(), z.string()),
});
/**
 * Ảnh mà tool `view_image` sẽ xem: tệp đã tải lên (`file_object`, URL ký theo
 * RBAC) hoặc bản thu nhỏ vừa PUT (`scratch`, khoá nằm dưới tiền tố của người gọi).
 */
export const ImageDescribeSource = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('file'), fileId: z.uuid() }),
    z.object({ kind: z.literal('scratch'), scratchId: z.uuid() }),
]);
/** Tham số của skill — cũng là `params` của golden case. */
export const ImageDescribeRequest = z.object({
    source: ImageDescribeSource,
    /** Alt hiện có (thường là tiêu đề trang web nguồn) — **dữ liệu**, không phải chỉ dẫn. */
    alt: z.string().trim().max(200),
    /** Rộng / cao; `null` = chưa đo. */
    ratio: z.number().min(0.05).max(20).nullable(),
});
/** `POST /me/files/image-description` — APM-SLD-n. */
export const ImageDescribeInput = ImageDescribeRequest.extend({
    runId: z.uuid().optional(),
});
export const ImageDescribeResult = z.object({
    description: ImageDescription,
    /** Chuỗi thập phân như mọi số tiền AI khác. */
    costUsd: z.string(),
    partial: z.boolean(),
});
// ── AI chỉ chủ thể cho hộp Xoá nền — APM-BGR-d, docs/plan-bg-removal.md §4.5, §6 ─────────────
/** Một điểm trên ảnh đã gửi cho AI — **pixel tuyệt đối**, không chuẩn hoá (Claude làm kém với toạ độ 0–1000). */
export const ImagePoint = z.object({
    x: z.number().int().min(0).max(IMAGE_DESCRIBE_MAX_EDGE),
    y: z.number().int().min(0).max(IMAGE_DESCRIBE_MAX_EDGE),
});
export const IMAGE_SUBJECT_HINT_MAX_CHARS = 200;
export const IMAGE_SUBJECT_MAX_POINTS = 5;
/** Tham số của skill `locate_subject` — cũng là `params` của golden case. */
export const ImageSubjectRequest = z.object({
    source: ImageDescribeSource,
    /** Cỡ **bản thu nhỏ** đã gửi (≤ 1024 cạnh dài) — hệ toạ độ của mọi điểm trả về. */
    width: z.number().int().min(1).max(IMAGE_DESCRIBE_MAX_EDGE),
    height: z.number().int().min(1).max(IMAGE_DESCRIBE_MAX_EDGE),
    /** Gợi ý người dùng gõ ("giữ cái cốc") — **dữ liệu**, không phải chỉ dẫn hệ thống. */
    hint: z.string().trim().max(IMAGE_SUBJECT_HINT_MAX_CHARS).default(''),
});
/**
 * Model chỉ chỗ, mô hình trên máy cắt (plan §4.5): hộp + vài điểm giữ / bỏ cho SlimSAM. Người dùng thấy chúng trên
 * ảnh và sửa được trước khi Áp dụng (luật #6).
 */
export const ImageSubject = z.object({
    /** Chủ thể là gì — một cụm ngắn, hiện cho người dùng biết AI hiểu gì. */
    subject: z.string().trim().min(1).max(120),
    box: z.object({
        x0: z.number().int().min(0),
        y0: z.number().int().min(0),
        x1: z.number().int().min(0),
        y1: z.number().int().min(0),
    }),
    keep: z.array(ImagePoint).min(1).max(IMAGE_SUBJECT_MAX_POINTS),
    drop: z.array(ImagePoint).max(IMAGE_SUBJECT_MAX_POINTS),
    /** `low` khi ảnh không có chủ thể rõ (cảnh rộng, nhiều vật ngang nhau). */
    confidence: z.enum(['high', 'low']),
});
/** `POST /me/files/image-background/locate` — APM-BGR-d. */
export const ImageSubjectInput = ImageSubjectRequest.extend({
    runId: z.uuid().optional(),
});
export const ImageSubjectResult = z.object({
    subject: ImageSubject,
    costUsd: z.string(),
    partial: z.boolean(),
});
//# sourceMappingURL=image-description.js.map